import ReactQuill from 'react-quill';
import Quill from 'quill';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import 'react-accessible-accordion/dist/fancy-example.css';
import { AccordionItemHeading, AccordionItemButton } from 'react-accessible-accordion';

import Actions from './props/Actions';
import { AttachmentUploader, AttachmentList, PendingAttachmentsList } from '../../../components/Attachments';
import { getMentionModule, setMentionMembers } from '../../../utils/workspaceUtils';

const Note = (props) => {
    const { activity, activityData, onUpdateActivity, onUpdateActivityMultiple } = props;
    const { currentWorkspace } = useSelector(state => state['Settings']);
    setMentionMembers(currentWorkspace?.['members'] || []);

    const [hovered, setHovered] = useState(false);
    const [dropdowns, setDropdowns] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [isSeeMoreText, setIsSeeMoreText] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);
    // Use local state for pending attachments to avoid cross-component sharing
    const [localPendingAttachments, setLocalPendingAttachments] = useState([]);

    const [activityState, setActivityState] = useState(activity);
    // Local state for description while editing to avoid controlled component conflicts
    const [editingDescription, setEditingDescription] = useState(activityState['description'] || '');
    
    useEffect(() => { 
        setActivityState(activity);
        // Update editing description when activity changes (but only if not currently editing)
        if (!isEditing) {
            setEditingDescription(activity['description'] || '');
        }
    }, [activity])

    useEffect(() => {
        if (textRef.current) {
            setIsOverflowing(textRef.current.scrollHeight > 168);
        }
    }, [activity?.description]);

    // Focus the quill editor when editing
    const quillRef = useRef(null);
    // Use a ref to track the latest editor content to avoid stale state issues
    const editorContentRef = useRef(activityState['description'] || '');
    
    // Update ref and editing state when activity state changes
    useEffect(() => {
        editorContentRef.current = activityState['description'] || '';
        // Only update editing description if not currently editing
        if (!isEditing) {
            setEditingDescription(activityState['description'] || '');
        }
    }, [activityState['description'], isEditing]);
    
    // Initialize editing description when starting to edit
    useEffect(() => {
        if (isEditing) {
            setEditingDescription(activityState['description'] || '');
        }
    }, [isEditing]);
    
    useEffect(() => {
        if (isEditing && quillRef.current) {
            const quill = quillRef.current.getEditor();
            quill.focus();
            // Set cursor to the end of the content
            const length = quill.getLength();
            quill.setSelection(length - 1, 0);
        }
    }, [isEditing])

    // Function to normalize HTML spacing and fix double spacing issues
    const normalizeHTMLSpacing = (html) => {
        if (!html) return '';
        
        // Create a temporary div to parse and normalize HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Remove extra line breaks and normalize spacing
        const normalizeElement = (element) => {
            // Remove empty paragraphs
            const emptyPs = element.querySelectorAll('p:empty');
            emptyPs.forEach(p => p.remove());
            
            // Normalize spacing in list items
            const listItems = element.querySelectorAll('li');
            listItems.forEach(li => {
                // Remove extra <br> tags at the start/end of list items
                let firstChild = li.firstChild;
                while (firstChild && (firstChild.nodeType === 3 && firstChild.textContent.trim() === '' || firstChild.nodeName === 'BR')) {
                    li.removeChild(firstChild);
                    firstChild = li.firstChild;
                }
                let lastChild = li.lastChild;
                while (lastChild && (lastChild.nodeType === 3 && lastChild.textContent.trim() === '' || lastChild.nodeName === 'BR')) {
                    li.removeChild(lastChild);
                    lastChild = li.lastChild;
                }
            });
            
            // Normalize spacing between paragraphs
            const paragraphs = element.querySelectorAll('p');
            paragraphs.forEach(p => {
                // Remove leading/trailing <br> tags
                let firstChild = p.firstChild;
                while (firstChild && firstChild.nodeName === 'BR') {
                    p.removeChild(firstChild);
                    firstChild = p.firstChild;
                }
                let lastChild = p.lastChild;
                while (lastChild && lastChild.nodeName === 'BR') {
                    p.removeChild(lastChild);
                    lastChild = p.lastChild;
                }
            });
        };
        
        normalizeElement(tempDiv);
        
        return tempDiv.innerHTML;
    };
    
    // Function to convert plain text URLs to clickable links and ensure existing links are styled
    const convertUrlsToLinks = (html) => {
        if (!html) return '';
        
        // First, ensure all existing links have proper styling
        let processed = html.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
            // Add class if not present
            if (!attributes.includes('class=')) {
                attributes += ' class="description-link"';
            }
            // Ensure target and rel attributes
            if (!attributes.includes('target=')) {
                attributes += ' target="_blank"';
            }
            if (!attributes.includes('rel=')) {
                attributes += ' rel="noopener noreferrer"';
            }
            return `<a ${attributes}>`;
        });
        
        // Regex to match URLs (http, https, www, or just domain patterns)
        const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"{}|\\^`\[\]]*)/gi;
        
        // Convert plain text URLs to links (but skip URLs already inside anchor tags)
        // We need to process this more carefully to avoid double-wrapping
        const urlMatches = [];
        let match;
        const tempRegex = new RegExp(urlRegex.source, urlRegex.flags);
        while ((match = tempRegex.exec(processed)) !== null) {
            urlMatches.push({
                text: match[0],
                index: match.index
            });
        }
        
        // Process matches in reverse order to preserve indices
        for (let i = urlMatches.length - 1; i >= 0; i--) {
            const urlMatch = urlMatches[i];
            const beforeMatch = processed.substring(0, urlMatch.index);
            const beforeLastAnchor = beforeMatch.lastIndexOf('<a ');
            const beforeLastAnchorClose = beforeMatch.lastIndexOf('</a>');
            
            // If we're inside an anchor tag, skip
            if (beforeLastAnchor > beforeLastAnchorClose) {
                continue;
            }
            
            // Ensure URL has protocol
            let url = urlMatch.text;
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
            }
            
                                    const linkHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="description-link">${urlMatch.text}</a>`;
            processed = processed.substring(0, urlMatch.index) + linkHTML + processed.substring(urlMatch.index + urlMatch.text.length);
        }
        
        return processed;
    };
    
    // Function to process HTML content for display
    const processHTMLForDisplay = (html) => {
        if (!html) return '';
        // First normalize spacing, then convert URLs
        let processed = normalizeHTMLSpacing(html);
        processed = convertUrlsToLinks(processed);
        return processed;
    };

    const toggleDropdownAction = (id) => setDropdowns((prev) => ({ ...prev, [id]: !prev[id] }));

    // Handlers for local attachment management
    const handleFilesAdded = (newFiles) => {
        setLocalPendingAttachments(prev => [...prev, ...newFiles]);
    };

    const handleFileRemoved = (fileId) => {
        setLocalPendingAttachments(prev => prev.filter(f => f.id !== fileId));
    };

    const handleSave = () => {
        setIsEditing(false);
        // Get content directly from Quill editor to ensure pasted content is captured
        let descriptionToSave = editorContentRef.current || activityState['description'] || '';
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            // Get the HTML content directly from the editor (most reliable source)
            descriptionToSave = quill.root.innerHTML;
            // Normalize spacing before saving
            descriptionToSave = normalizeHTMLSpacing(descriptionToSave);
            // Update ref and state with the actual editor content
            editorContentRef.current = descriptionToSave;
            setActivityState(prev => ({ ...prev, description: descriptionToSave }));
        }

        // Update description and attachments together
        if (onUpdateActivityMultiple && localPendingAttachments.length > 0) {
            // Update both description and attachments in a single call
            onUpdateActivityMultiple({
                description: descriptionToSave,
                attachments: localPendingAttachments
            });
            // Clear local attachments after saving
            setLocalPendingAttachments([]);
        } else {
            // Only update description if no new attachments
            onUpdateActivity('description', descriptionToSave);
        }
    };
    // Register URL matcher for auto-linking URLs in Quill
    useEffect(() => {
        // Define URL matcher for Quill
        const Link = Quill.import('formats/link');
        
        // Custom URL matcher that auto-converts URLs to links
        const urlMatcher = (text, index) => {
            const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"{}|\\^`\[\]]*)/gi;
            const match = text.substring(index).match(urlRegex);
            if (match) {
                return {
                    index: index + text.substring(index).indexOf(match[0]),
                    length: match[0].length,
                    data: {
                        url: match[0].startsWith('http://') || match[0].startsWith('https://') 
                            ? match[0] 
                            : 'https://' + match[0]
                    }
                };
            }
            return null;
        };
        
        // Register the matcher
        if (Quill.imports && Quill.imports['modules/link']) {
            const LinkModule = Quill.import('modules/link');
            if (LinkModule && !LinkModule.matchers) {
                LinkModule.matchers = [];
            }
            if (LinkModule && LinkModule.matchers) {
                LinkModule.matchers.push(urlMatcher);
            }
        }
    }, []);
    
    // Unique toolbar ID per activity to avoid conflicts
    const toolbarId = `custom-toolbar-note-${activity?.['_id'] || 'new'}`;

    // Enhanced editor modules with proper clipboard handling for paste events
    const editorModules = {
        toolbar: `#${toolbarId}`,
        clipboard: {
            matchVisual: false,
            allowed: {
                tags: ['p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'blockquote', 'a', 'img'],
                attributes: ['href', 'src', 'alt', 'style']
            }
        },
        mention: getMentionModule()
    };
    const formats = ['font', 'size', 'color', 'background', 'align', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'link', 'image', 'align', 'mention'];

    return (
        <>
            <AccordionItemHeading>
                <AccordionItemButton>
                    <div className={`title-area`}>
                        <p><span>Note</span> by {activityData?.['createdBy']}</p>
                        <div className='right-siede' onClick={(e) => e.stopPropagation()}>
                            <Actions {...props} />
                            <p className="text-gray">{activityData?.['createdAt']}</p>
                        </div>
                    </div>
                    <div className={`title-area editing-title ${isEditing ? 'is-editing' : ''}`} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                        {isEditing ?
                            <div className="edit-wrapper">
                                <div className="editor-wrapper" onClick={(e) => e.stopPropagation()}>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow" // must be snow for custom toolbar
                                        formats={formats}
                                        modules={editorModules}
                                        bounds=".editor-wrapper"
                                        value={editingDescription}
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e['key'] === 'Escape') setIsEditing(false);
                                        }}
                                        onChange={(value) => {
                                            // Use functional update to ensure state is properly updated
                                            const newValue = value || '';
                                            // Update ref immediately
                                            editorContentRef.current = newValue;
                                            // Update local editing state immediately (this updates the value prop)
                                            // This must be synchronous to prevent ReactQuill from resetting
                                            setEditingDescription(newValue);
                                            // Update main state
                                            setActivityState(prev => {
                                                if (prev.description !== newValue) {
                                                    return { ...prev, description: newValue };
                                                }
                                                return prev;
                                            });
                                        }}
                                        placeholder="Start typing to leave a note..."
                                    />

                                    {/* Custom Toolbar - MUST BE visible */}
                                    <div id={toolbarId} className="bottom-toolbar">
                                        <span className="ql-formats">
                                            <button className="ql-bold" />
                                            <button className="ql-italic" />
                                            <button className="ql-underline" />
                                            <button className="ql-strike" />
                                        </span>
                                        <div className="ql-formats more-dropdown">
                                            <button className="more-toggle" onClick={() => toggleDropdownAction('showMore')} >More ▾</button>
                                            <span
                                                className={`more-dropdown-content ${dropdowns.showMore ? 'show' : ''}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <svg data-test-id="popover-arrow" className="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28"><path clipPath="url(#:ra5:)" fill="none" stroke="#CBD6E2" strokeWidth="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                                                <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                                                    <select className="ql-font" />
                                                    <select className="ql-size" />
                                                    <select className="ql-color" />
                                                    <select className="ql-background" />
                                                    <button className="ql-blockquote" />
                                                    <button className="ql-list" value="ordered" />
                                                    <select className="ql-align" />
                                                </span>
                                            </span>
                                        </div>
                                        <span className="ql-formats">
                                            <button className="ql-list" value="ordered" />
                                            <button className="ql-list" value="bullet" />
                                        </span>
                                        <span className="ql-formats">
                                            <button className="ql-link" />
                                            <button className="ql-image" />
                                        </span>
                                        <div className="attchemnt-inner-ql">
                                            <AttachmentUploader
                                                existingFiles={localPendingAttachments}
                                                maxFiles={5}
                                                useLocalState={true}
                                                iconOnly={true}
                                                onFilesAdded={handleFilesAdded}
                                                onFileRemoved={handleFileRemoved}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <PendingAttachmentsList
                                    files={localPendingAttachments}
                                    onRemove={handleFileRemoved}
                                />
                                <div className="edit-actions">
                                    <button className="btn-style-new" onClick={handleSave}>Save</button>
                                    <button className="btn-style-border" onClick={() => setIsEditing(false)}>Cancel</button>
                                </div>
                            </div>
                            :
                            <span className="title-text position-relative" ref={textRef} onClick={(e) => {
                                // Don't trigger edit mode if clicking on a link
                                if (e.target.tagName === 'A' || e.target.closest('a')) {
                                    return;
                                }
                                setIsEditing(true);
                            }}>
                                {isSeeMoreText && isOverflowing ? (
                                    <div className="description-text-container">
                                        <p 
                                            className="description-text expanded" 
                                            dangerouslySetInnerHTML={{ __html: processHTMLForDisplay(activity?.['description'] || '') }} 
                                        ></p>
                                    </div>
                                ) : (
                                    <p 
                                        className={`description-text ${isOverflowing ? 'collapsed' : ''}`} 
                                        dangerouslySetInnerHTML={{ __html: processHTMLForDisplay(activity?.['description'] || '') }} 
                                    ></p>
                                )}
                                {hovered && <i className="edit-icon"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="Edit" className="StyledIcon-sc-1jfhstb-0 iTgcdA"><path d="m11.86 27.84.25-.1h.1c.08-.05.16-.11.23-.17l12.15-12.08c.28-.28.44-.65.43-1.05-.02-.84-.71-1.5-1.55-1.48-.38 0-.75.16-1.03.43L11.43 24.3l-.88-.87 10.97-10.87c.5-.66.38-1.6-.28-2.11a1.5 1.5 0 0 0-1.82 0L8.41 21.32l-.89-.87L18.54 9.56c.53-.64.45-1.59-.19-2.12a1.51 1.51 0 0 0-1.94.01L4.32 19.38c-.12.13-.21.27-.28.43v.14s.01.08 0 .13l-2.02 7.98q-.03.18 0 .36c0 .82.68 1.49 1.5 1.48.12.02.25.02.38 0l8.07-1.98-.1-.09ZM25.93 3.06a3.527 3.527 0 0 0-4.9 0L19.42 4.6c-.28.28-.44.66-.44 1.06s.16.77.44 1.05l5.87 5.8c.59.59 1.55.59 2.14 0l1.56-1.54a3.416 3.416 0 0 0 0-4.84l-3.07-3.07Z"></path></svg></i>}
                                {isOverflowing && (
                                    <button
                                        className="see-more-btn"
                                        onClick={(e) => { setIsSeeMoreText(!isSeeMoreText); e.stopPropagation(); }}
                                    >
                                        {isSeeMoreText ? 'Hide' : 'See More'}
                                    </button>
                                )}
                            </span>
                        }

                    </div>
                    {/* Attachments Section */}
                    {activity['attachments'] && activity['attachments'].length > 0 && (
                        <div className="activity-attachments margin-top-sm">
                            <AttachmentList
                                attachments={activity['attachments']}
                                showUploadedBy={false}
                                enableActions={false}
                            />
                        </div>
                    )}
                </AccordionItemButton>
            </AccordionItemHeading>
        </>
    )
}

export default Note;